import { useEffect, useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { inboundWebhookPublicPath, publicAppOrigin } from "@/lib/publicAppOrigin";
import { slugifyInboundWebhookName } from "@/lib/ops/inboundWebhookSlug";
import {
  createOpsInboundWebhook,
  listOpsInboundWebhooks,
  setOpsInboundWebhookActive,
  type OpsInboundWebhook,
} from "@/lib/opsInboundWebhooksClient";
import { OpsCopyableReadonlyField } from "./OpsCopyableReadonlyField";

const btnClass = "rounded border px-3 py-1 text-sm";

export function OpsInboundWebhooksSection(props: {
  token: string | null;
  busy: boolean;
  onRotate: () => void;
}) {
  const token = props.token?.trim() || "";
  const origin = publicAppOrigin();
  const [items, setItems] = useState<OpsInboundWebhook[]>([]);
  const [canDeactivate, setCanDeactivate] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const previewSlug = slugifyInboundWebhookName(name);

  async function reload() {
    const r = await listOpsInboundWebhooks();
    if (!r.ok) {
      setLoadError(r.error);
      return;
    }
    setLoadError(null);
    setItems(r.data.items ?? []);
    setCanDeactivate(Boolean(r.data.can_deactivate));
  }

  useEffect(() => {
    void reload();
  }, []);

  async function onAccept() {
    setFormError(null);
    setFormBusy(true);
    const r = await createOpsInboundWebhook(name);
    setFormBusy(false);
    if (!r.ok) {
      setFormError(r.error);
      return;
    }
    setName("");
    await reload();
  }

  async function onSetActive(row: OpsInboundWebhook, isActive: boolean) {
    setFormError(null);
    const verb = isActive ? "reactivate" : "deactivate";
    if (!window.confirm(`${verb[0].toUpperCase()}${verb.slice(1)} “${row.display_name}”? The URL stays; posts will ${isActive ? "work again" : "401"}.`)) {
      return;
    }
    const r = await setOpsInboundWebhookActive(row.id, isActive);
    if (!r.ok) {
      setFormError(r.error);
      return;
    }
    await reload();
  }

  function urlFor(slug: string): string {
    if (!token) return "";
    const path = inboundWebhookPublicPath(slug, token);
    return origin ? `${origin}${path}` : path;
  }

  return (
    <div className="min-w-0 space-y-3">
      <InlineHelp label="How inbound webhooks work">
        Same org token on every URL. Name a source, Accept, then paste that URL into the vendor. Posts
        are stored as the original JSON. Owner/dev can deactivate (never delete).
      </InlineHelp>
      {token ? <OpsCopyableReadonlyField label="Token" value={token} /> : null}
      {loadError ? <p className="text-sm text-amber-800">{loadError}</p> : null}
      {items.map((row) => (
        <div key={row.id} className="min-w-0 space-y-1 rounded border border-gray-200 px-2 py-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-medium text-gray-700">
              {row.display_name}
              <span className="ml-2 font-mono font-normal text-gray-500">{row.slug}</span>
              {!row.is_active ? <span className="ml-2 text-amber-800">inactive</span> : null}
            </p>
            {canDeactivate ? (
              <button
                type="button"
                className={btnClass}
                onClick={() => void onSetActive(row, !row.is_active)}
              >
                {row.is_active ? "Deactivate" : "Reactivate"}
              </button>
            ) : null}
          </div>
          {token ? (
            <OpsCopyableReadonlyField label="Public URL" value={urlFor(row.slug)} />
          ) : (
            <p className="text-xs text-gray-500">Generate a token to copy the URL.</p>
          )}
        </div>
      ))}
      <div className="space-y-2 rounded border border-dashed border-gray-300 px-2 py-2">
        <p className="text-xs font-medium text-gray-700">New inbound webhook</p>
        <label className="block text-xs text-gray-600">
          Inbound name
          <input
            className="mt-1 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name of the incoming data source"
            disabled={formBusy}
          />
        </label>
        {previewSlug ? (
          <p className="break-all font-mono text-[11px] text-gray-500">
            /api/integrations/{previewSlug}/webhook?token=…
          </p>
        ) : null}
        <button type="button" className={btnClass} disabled={formBusy || !previewSlug} onClick={() => void onAccept()}>
          {formBusy ? "Saving…" : "Accept"}
        </button>
      </div>
      {formError ? <p className="text-sm text-amber-800">{formError}</p> : null}
      <button
        type="button"
        className={btnClass}
        disabled={props.busy}
        onClick={() => {
          if (
            token &&
            !window.confirm(
              "Rotate the webhook token? Update every inbound URL that already uses this token or events will 401."
            )
          ) {
            return;
          }
          props.onRotate();
        }}
      >
        {token ? "Rotate token" : "Generate token"}
      </button>
    </div>
  );
}
